import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WizardCandidate } from '../../campaign-wizard.types';
import { wz } from '../../components/wizard-tokens';
import { WizGradientButton, WizLink, WizOption } from '../../components/wizard-ui';

export type ContactSex = 'female' | 'male';

interface Props {
  /** Kandidat mit unklarem Geschlecht; null = geschlossen. */
  candidate: WizardCandidate | null;
  onClose: () => void;
  /** Speichert das Geschlecht; true = gespeichert (Dialog schließt). */
  onSave: (candidate: WizardCandidate, sex: ContactSex) => Promise<boolean>;
}

/**
 * „Geschlecht festlegen“ (wie `UpdateSexDialog` der alten Pipeline): Link zum LinkedIn-Profil zum Nachsehen,
 * dann weiblich / männlich. Ohne Vorauswahl – geraten wird hier nicht.
 */
const SexDialog: React.FC<Props> = ({ candidate, onClose, onSave }) => {
  const { t } = useTranslation();
  const [sex, setSex] = useState<ContactSex | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSex(null);
    setSaving(false);
  }, [candidate?.id]);

  const save = async () => {
    if (!candidate || !sex) return;
    setSaving(true);
    const ok = await onSave(candidate, sex);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Dialog
      open={candidate !== null}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>{t('wizListSexTitle')}</DialogTitle>
      <DialogContent sx={{ paddingTop: '8px !important' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 14, color: wz.muted }}>{t('wizListSexBody')}</div>
          {candidate?.linkedInUrl ? (
            <a
              href={candidate.linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: wz.primary, fontSize: 15, fontWeight: 600 }}
              data-testid="wiz-sex-profile"
            >
              {candidate.name}
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </a>
          ) : (
            <div style={{ fontSize: 15, fontWeight: 600 }}>{candidate?.name}</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 10 }}>
            <WizOption selected={sex === 'female'} onClick={() => setSex('female')} label={t('wizListSexFemale')} />
            <WizOption selected={sex === 'male'} onClick={() => setSex('male')} label={t('wizListSexMale')} />
          </div>
        </div>
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 20px', gap: '16px' }}>
        <WizLink onClick={onClose}>{t('wizListSexCancel')}</WizLink>
        <WizGradientButton onClick={() => void save()} disabled={!sex} loading={saving}>
          {t('wizListSexSave')}
        </WizGradientButton>
      </DialogActions>
    </Dialog>
  );
};

export default SexDialog;
